import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, ChevronRight, Shield,
  LayoutDashboard, Fingerprint, Mail, MapPin, Globe,
  Banknote, Scale, ShieldAlert, Newspaper, Network, ClipboardList,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCDDCase } from '@/hooks/profiler/useCDDCase';
import { RiskScoreRing } from '@/components/profiler/RiskScoreRing';
import { RiskTierBadge } from '@/components/profiler/RiskTierBadge';
import { ReportDownloadBar } from '@/components/profiler/ReportDownloadBar';
import { TIER_COLOR } from '@/types/profiler';

import { Overview }      from '@/components/profiler/DossierSection/Overview';
import { PIIIdentity }   from '@/components/profiler/DossierSection/PIIIdentity';
import { Contacts }      from '@/components/profiler/DossierSection/Contacts';
import { Addresses }     from '@/components/profiler/DossierSection/Addresses';
import { Digital }       from '@/components/profiler/DossierSection/Digital';
import { Financial }     from '@/components/profiler/DossierSection/Financial';
import { Legal }         from '@/components/profiler/DossierSection/Legal';
import { Sanctions }     from '@/components/profiler/DossierSection/Sanctions';
import { AdverseMedia }  from '@/components/profiler/DossierSection/AdverseMedia';
import { NetworkGraph }  from '@/components/profiler/DossierSection/NetworkGraph';
import { IntelSummary }  from '@/components/profiler/DossierSection/IntelSummary';

import type { CDDReport, PipelineResult, RiskScore, IdentifierCluster } from '@/types/profiler';

// ── tab definition ────────────────────────────────────────────────────────────

interface Tab {
  id: string;
  label: string;
  icon: LucideIcon;
}

const TABS: Tab[] = [
  { id: 'overview',    label: 'Overview',      icon: LayoutDashboard },
  { id: 'pii',         label: 'PII & Identity', icon: Fingerprint },
  { id: 'contacts',    label: 'Contacts',      icon: Mail },
  { id: 'addresses',   label: 'Addresses',     icon: MapPin },
  { id: 'digital',     label: 'Digital',       icon: Globe },
  { id: 'financial',   label: 'Financial',     icon: Banknote },
  { id: 'legal',       label: 'Legal',         icon: Scale },
  { id: 'sanctions',   label: 'Sanctions',     icon: ShieldAlert },
  { id: 'adverse',     label: 'Adverse Media', icon: Newspaper },
  { id: 'network',     label: 'Network',       icon: Network },
  { id: 'intel',       label: 'Intel Summary', icon: ClipboardList },
];

// ── helpers ───────────────────────────────────────────────────────────────────

function initials(name?: string): string {
  if (!name) return '?';
  return name.split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');
}

// ── DossierSidebar ────────────────────────────────────────────────────────────

interface SidebarProps {
  report: CDDReport;
  pipeline?: PipelineResult;
  risk?: RiskScore;
  cluster?: IdentifierCluster;
  userRole?: string;
}

function DossierSidebar({ report, risk, cluster, userRole }: SidebarProps) {
  const tier = report.risk_tier;
  const tierColor = TIER_COLOR[tier];
  const canonicalName = report.seed ?? cluster?.canonical ?? '—';
  const score = risk?.overall_score ?? report.risk_score?.overall_score ?? report.overall_score ?? 0;

  const variantCount = cluster?.variants?.length ?? 0;
  const enrichmentRounds = Math.max(1, Math.round(variantCount / 3));

  return (
    <div
      style={{
        width: 220,
        flexShrink: 0,
        background: '#090D13',
        border: '1px solid #182030',
        borderRadius: 10,
        position: 'sticky',
        top: 20,
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      {/* Entity section */}
      <div style={{ padding: '16px 14px', borderBottom: '1px solid #182030' }}>
        {/* Avatar */}
        <div
          style={{
            width: 52, height: 52,
            background: `${tierColor}20`,
            border: `2px solid ${tierColor}50`,
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 10px',
          }}
        >
          <span style={{ color: tierColor, fontWeight: 800, fontSize: 16, fontFamily: 'monospace' }}>
            {initials(canonicalName)}
          </span>
        </div>

        {/* Name */}
        <p
          style={{
            color: '#DCE8F5', fontWeight: 700, fontSize: 12, textAlign: 'center',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            marginBottom: 6,
          }}
          title={canonicalName}
        >
          {canonicalName}
        </p>

        {/* Tier + score */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 6 }}>
          <RiskTierBadge tier={tier} pulse={tier === 'CRITICAL'} size="sm" />
          <RiskScoreRing score={score} tier={tier} size={40} showScore />
        </div>
      </div>

      {/* Stats */}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #182030' }}>
        {[
          { label: 'VARIANTS', value: String(variantCount) },
          { label: 'ENRICHMENT ROUNDS', value: String(enrichmentRounds) },
        ].map(stat => (
          <div key={stat.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.08em' }}>
              {stat.label}
            </span>
            <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', fontWeight: 700 }}>
              {stat.value}
            </span>
          </div>
        ))}
        <div style={{ marginTop: 4 }}>
          <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.08em', display: 'block', marginBottom: 2 }}>
            CASE REF
          </span>
          <span style={{ color: '#5A7A9A', fontSize: 8, fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {report.case_reference}
          </span>
        </div>
      </div>

      {/* Download bar */}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #182030' }}>
        <p style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.08em', marginBottom: 8 }}>
          REPORTS
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <ReportDownloadBar
            caseId={report.case_id}
            caseRef={report.case_reference}
            userRole={userRole}
          />
        </div>
      </div>

      {/* TLP classification */}
      <div style={{ padding: '10px 14px', marginTop: 'auto' }}>
        <span
          style={{
            display: 'block',
            textAlign: 'center',
            background: '#1A1000',
            color: '#F0B429',
            border: '1px solid #F0B42940',
            fontSize: 9,
            fontFamily: 'monospace',
            fontWeight: 800,
            padding: '5px 0',
            borderRadius: 6,
            letterSpacing: '0.12em',
          }}
        >
          TLP:AMBER
        </span>
      </div>
    </div>
  );
}

// ── section renderer ──────────────────────────────────────────────────────────

interface SectionRendererProps {
  activeTab: string;
  report: CDDReport;
  pipeline?: PipelineResult;
  risk?: RiskScore;
  cluster?: IdentifierCluster;
}

function SectionRenderer({ activeTab, report, pipeline, risk, cluster }: SectionRendererProps) {
  const props = { report, pipeline, risk, cluster };
  switch (activeTab) {
    case 'overview':  return <Overview {...props} />;
    case 'pii':       return <PIIIdentity {...props} />;
    case 'contacts':  return <Contacts {...props} />;
    case 'addresses': return <Addresses {...props} />;
    case 'digital':   return <Digital {...props} />;
    case 'financial': return <Financial {...props} />;
    case 'legal':     return <Legal {...props} />;
    case 'sanctions': return <Sanctions {...props} />;
    case 'adverse':   return <AdverseMedia {...props} />;
    case 'network':   return <NetworkGraph {...props} />;
    case 'intel':     return <IntelSummary {...props} />;
    default:          return <Overview {...props} />;
  }
}

// ── loading skeleton ──────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div style={{ background: '#050709', minHeight: '100vh' }} className="flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <Loader2 size={28} className="animate-spin" style={{ color: '#00C8FF' }} />
        <p style={{ color: '#5A7A9A', fontSize: 11, fontFamily: 'monospace' }}>
          Loading dossier…
        </p>
      </div>
    </div>
  );
}

// ── error card ────────────────────────────────────────────────────────────────

function ErrorCard({ caseId }: { caseId?: string }) {
  const navigate = useNavigate();
  return (
    <div style={{ background: '#050709', minHeight: '100vh' }} className="flex items-center justify-center">
      <div
        style={{ background: '#090D13', border: '1px solid #FF1F1F30', borderRadius: 12 }}
        className="p-8 text-center max-w-sm"
      >
        <Shield size={32} style={{ color: '#FF1F1F' }} className="mx-auto mb-3" />
        <p style={{ color: '#DCE8F5', fontSize: 13, fontFamily: 'monospace', fontWeight: 700, marginBottom: 4 }}>
          Dossier Not Found
        </p>
        <p style={{ color: '#5A7A9A', fontSize: 10, fontFamily: 'monospace', marginBottom: 16 }}>
          Case {caseId ?? ''} could not be loaded.
        </p>
        <button
          onClick={() => navigate('/app/profiler')}
          style={{ background: '#00C8FF18', color: '#00C8FF', border: '1px solid #00C8FF44' }}
          className="text-[10px] font-mono px-4 py-2 rounded-lg"
        >
          BACK TO PROFILER
        </button>
      </div>
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function DossierPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<string>('overview');

  const { data: report, isLoading, isError } = useCDDCase(caseId);

  if (isLoading) return <LoadingSkeleton />;
  if (isError || !report) return <ErrorCard caseId={caseId} />;

  const pipeline = report.pipeline_result;
  const risk = report.risk_score;
  const cluster = report.identifier_cluster ?? pipeline?.cluster;

  return (
    <div style={{ background: '#050709', minHeight: '100vh' }}>
      {/* Top nav bar */}
      <div style={{ background: '#090D13', borderBottom: '1px solid #182030' }}>
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/app/profiler')}
            style={{ color: '#5A7A9A' }}
            className="text-[10px] font-mono hover:opacity-80 transition-opacity"
          >
            PROFILER
          </button>
          <ChevronRight size={10} style={{ color: '#3A5A7A' }} />
          <button
            onClick={() => navigate(`/app/profiler/case/${caseId}`)}
            style={{ color: '#5A7A9A' }}
            className="text-[10px] font-mono hover:opacity-80 transition-opacity"
          >
            {report.case_reference}
          </button>
          <ChevronRight size={10} style={{ color: '#3A5A7A' }} />
          <span style={{ color: '#DCE8F5' }} className="text-[10px] font-mono">
            DOSSIER
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}
        >
          {/* Left sidebar */}
          <DossierSidebar
            report={report}
            pipeline={pipeline}
            risk={risk}
            cluster={cluster}
            userRole={user?.role}
          />

          {/* Main area */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Tab strip */}
            <div
              style={{
                background: '#090D13',
                border: '1px solid #182030',
                borderRadius: '10px 10px 0 0',
                borderBottom: 'none',
                overflowX: 'auto',
                scrollbarWidth: 'none',
              }}
            >
              <div style={{ display: 'flex', padding: '0 8px', minWidth: 'max-content' }}>
                {TABS.map(tab => {
                  const active = activeTab === tab.id;
                  const IconComp = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '12px 14px',
                        color: active ? '#00C8FF' : '#5A7A9A',
                        fontSize: 10,
                        fontFamily: 'monospace',
                        fontWeight: active ? 700 : 400,
                        background: 'none',
                        border: 'none',
                        borderBottom: active ? '2px solid #00C8FF' : '2px solid transparent',
                        cursor: 'pointer',
                        transition: 'color 0.15s',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <IconComp size={12} style={{ color: active ? '#00C8FF' : '#5A7A9A', flexShrink: 0 }} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Section content */}
            <div
              style={{
                background: '#090D13',
                border: '1px solid #182030',
                borderTop: '1px solid #182030',
                borderRadius: '0 0 10px 10px',
                padding: '20px',
              }}
            >
              <SectionRenderer
                key={activeTab}
                activeTab={activeTab}
                report={report}
                pipeline={pipeline}
                risk={risk}
                cluster={cluster}
              />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
